import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { decodeBlurhashToDataURL, isValidBlurhash } from '../utils/blurhash';

interface BlurhashImageProps {
  src: string;
  blurhash?: string;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  objectFit?: 'contain' | 'cover' | 'fill';
  onLoad?: () => void;
  onClick?: () => void;
}

const ImageContainer = styled.div<{ $blurhashBg?: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #F6F6F6;
  background-image: ${({ $blurhashBg }) => $blurhashBg ? `url(${$blurhashBg})` : 'none'};
  background-size: cover;
  background-position: center;
  overflow: hidden;
  @media (max-width: 1081px) {
    width: 100%;
    border-radius: 16px;
    background: #FFFFFF;

  }
`;

const StyledImage = styled.img<{ $loaded: boolean; $objectFit: string }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $objectFit }) => $objectFit};
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

export const BlurhashImage: React.FC<BlurhashImageProps> = ({
  src,
  blurhash,
  alt = '',
  className,
  style,
  objectFit = 'contain',
  onLoad,
  onClick,
}) => {
  const [loaded, setLoaded] = useState(false);

  // Memoize blurhash decoding to avoid re-computing on every render
  const blurhashDataURL = useMemo(() => {
    if (isValidBlurhash(blurhash)) {
      return decodeBlurhashToDataURL(blurhash!, 32, 32);
    }
    return '';
  }, [blurhash]);

  // Reset loaded state when src changes
  useEffect(() => {
    setLoaded(false);
  }, [src]);

  const handleLoad = () => {
    setLoaded(true);
    onLoad?.();
  };

  return (
    <ImageContainer
      $blurhashBg={blurhashDataURL}
      className={className}
      style={style}
      onClick={onClick}
    >
      <StyledImage
        src={src}
        alt={alt}
        $loaded={loaded}
        $objectFit={objectFit}
        onLoad={handleLoad}
      />
    </ImageContainer>
  );
};

export default BlurhashImage;
